<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useKanbanPipelinesStore } from 'dashboard/stores/kanbanPipelines';
import { useKanbanStagesStore } from 'dashboard/stores/kanbanStages';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KanbanBoard from 'dashboard/components-next/Kanban/KanbanBoard.vue';
import KanbanStageDialog from 'dashboard/components-next/Kanban/KanbanStageDialog.vue';
import KanbanStageSettingsList from 'dashboard/components-next/Kanban/KanbanStageSettingsList.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const kanbanPipelinesStore = useKanbanPipelinesStore();
const kanbanStagesStore = useKanbanStagesStore();

const pipelineId = computed(() => Number(route.params.pipelineId));
const pipelines = computed(() => kanbanPipelinesStore.getPipelines);
const activePipeline = computed(() =>
  pipelines.value.find(p => p.id === pipelineId.value)
);
const stages = computed(() =>
  kanbanStagesStore.getStagesByPipeline(pipelineId.value)
);
const uiFlags = computed(() => kanbanStagesStore.getUIFlags);

const settingsDialogRef = ref(null);
const stageDialogRef = ref(null);
const deleteStageDialogRef = ref(null);
const stageToDelete = ref(null);

onMounted(() => {
  if (!pipelines.value.length) kanbanPipelinesStore.get();
});

const switchPipeline = pipeline => {
  router.push(accountScopedRoute('kanban_board', { pipelineId: pipeline.id }));
};

const openSettings = () => settingsDialogRef.value?.open();
const openCreateStageDialog = () => stageDialogRef.value?.open();
const openEditStageDialog = stage => stageDialogRef.value?.open(stage);

const handleStageConfirm = async ({ id, name, color }) => {
  try {
    if (id) {
      await kanbanStagesStore.update({
        pipelineId: pipelineId.value,
        id,
        name,
        color,
      });
      useAlert(t('KANBAN.STAGES.EDIT.SUCCESS_MESSAGE'));
    } else {
      await kanbanStagesStore.create({
        pipelineId: pipelineId.value,
        name,
        color,
      });
      useAlert(t('KANBAN.STAGES.ADD.SUCCESS_MESSAGE'));
    }
    stageDialogRef.value?.onSuccess();
  } catch (error) {
    useAlert(
      id
        ? t('KANBAN.STAGES.EDIT.ERROR_MESSAGE')
        : t('KANBAN.STAGES.ADD.ERROR_MESSAGE')
    );
  }
};

const openDeleteStageDialog = stage => {
  stageToDelete.value = stage;
  deleteStageDialogRef.value?.open();
};

const handleStageDelete = async () => {
  try {
    await kanbanStagesStore.delete({
      pipelineId: pipelineId.value,
      id: stageToDelete.value.id,
    });
    useAlert(t('KANBAN.STAGES.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('KANBAN.STAGES.DELETE.ERROR_MESSAGE'));
  } finally {
    deleteStageDialogRef.value?.close();
  }
};

const handleReorder = async positionsHash => {
  try {
    await kanbanStagesStore.reorder({
      pipelineId: pipelineId.value,
      positionsHash,
    });
  } catch (error) {
    useAlert(t('KANBAN.STAGES.REORDER_ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full gap-3 p-6 overflow-hidden">
    <div class="flex items-center justify-between gap-3">
      <div class="flex items-center gap-2 overflow-x-auto">
        <button
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          class="px-3 py-1.5 text-sm rounded-lg whitespace-nowrap"
          :class="
            pipeline.id === pipelineId
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-solid-2 text-n-slate-12 hover:bg-n-solid-3'
          "
          @click="switchPipeline(pipeline)"
        >
          {{ pipeline.name }}
        </button>
      </div>
      <Button
        :label="t('KANBAN.BOARD.SETTINGS')"
        icon="i-lucide-settings"
        variant="outline"
        slate
        @click="openSettings"
      />
    </div>

    <KanbanBoard
      v-if="activePipeline"
      :pipeline-id="pipelineId"
      @edit-stage="openEditStageDialog"
    />

    <Dialog
      ref="settingsDialogRef"
      :title="t('KANBAN.STAGES.HEADER')"
      width="lg"
      :show-confirm-button="false"
      :cancel-button-label="t('DIALOG.BUTTONS.CLOSE')"
    >
      <div class="flex flex-col gap-3">
        <KanbanStageSettingsList
          :stages="stages"
          @edit="openEditStageDialog"
          @delete="openDeleteStageDialog"
          @reorder="handleReorder"
        />
        <Button
          type="button"
          :label="t('KANBAN.STAGES.ADD.TITLE')"
          icon="i-lucide-plus"
          variant="outline"
          class="self-start"
          @click="openCreateStageDialog"
        />
      </div>
    </Dialog>

    <KanbanStageDialog
      ref="stageDialogRef"
      :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
      @confirm="handleStageConfirm"
    />

    <Dialog
      ref="deleteStageDialogRef"
      type="alert"
      :title="t('KANBAN.STAGES.DELETE.TITLE')"
      :description="t('KANBAN.STAGES.DELETE.CONFIRM')"
      :is-loading="uiFlags.deletingItem"
      @confirm="handleStageDelete"
    />
  </div>
</template>
